import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, TrendingUp, TrendingDown, CalendarDays, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtR = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const isoToday = () => new Date().toISOString().slice(0, 10);
const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const RANGE_PRESETS = [
  { key: "3m", label: "3 months", days: 90 },
  { key: "6m", label: "6 months", days: 180 },
  { key: "12m", label: "12 months", days: 365 },
  { key: "custom", label: "Custom", days: null },
];

const TAB_KEYS = {
  dow: "day_of_week",
  dom: "day_of_month",
  wom: "week_of_month",
};

const Highlights = ({ h }) => {
  if (!h || (!h.busiest && !h.slowest)) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
      {h.busiest && (
        <div className="p-3 rounded-lg border bg-green-50 border-green-200 flex items-start gap-3" data-testid="pattern-busiest">
          <TrendingUp className="h-5 w-5 text-green-600 mt-0.5" />
          <div>
            <div className="text-xs font-medium text-green-700 uppercase tracking-wide">Busiest</div>
            <div className="text-lg font-bold text-green-900">{h.busiest.label}</div>
            <div className="text-xs text-green-700">{fmtR(h.busiest.avg_sales_per_day)} / day · {h.busiest.avg_tx_per_day} tx</div>
          </div>
        </div>
      )}
      {h.slowest && (
        <div className="p-3 rounded-lg border bg-orange-50 border-orange-200 flex items-start gap-3" data-testid="pattern-slowest">
          <TrendingDown className="h-5 w-5 text-orange-600 mt-0.5" />
          <div>
            <div className="text-xs font-medium text-orange-700 uppercase tracking-wide">Slowest</div>
            <div className="text-lg font-bold text-orange-900">{h.slowest.label}</div>
            <div className="text-xs text-orange-700">{fmtR(h.slowest.avg_sales_per_day)} / day · {h.slowest.avg_tx_per_day} tx</div>
          </div>
        </div>
      )}
    </div>
  );
};

const PatternChart = ({ rows, xKey = "label", testId }) => {
  const data = rows.map((r) => ({
    label: r.label,
    "Avg Sales (R)": r.avg_sales_per_day,
    "Avg Transactions": r.avg_tx_per_day,
  }));
  return (
    <div data-testid={testId} className="w-full" style={{ height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} interval={0} angle={rows.length > 10 ? -45 : 0} textAnchor={rows.length > 10 ? "end" : "middle"} height={rows.length > 10 ? 60 : 30} />
          <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => `R${(v / 1000).toFixed(0)}k`} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(v, name) => (name === "Avg Sales (R)" ? fmtR(v) : Number(v).toFixed(1))}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar yAxisId="left" dataKey="Avg Sales (R)" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
          <Bar yAxisId="right" dataKey="Avg Transactions" fill="#f59e0b" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const SalesPatternCard = () => {
  const [preset, setPreset] = useState("12m");
  const [startDate, setStartDate] = useState(isoDaysAgo(365));
  const [endDate, setEndDate] = useState(isoToday());
  const [tab, setTab] = useState("dow");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [insight, setInsight] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      const res = await axios.get(`${API}/sales-patterns/analysis`, { params });
      setData(res.data);
      setInsight(""); // reset when range changes
    } catch (e) {
      console.error(e);
      toast.error("Failed to load sales pattern");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyPreset = (key) => {
    setPreset(key);
    if (key === "custom") return;
    const p = RANGE_PRESETS.find((x) => x.key === key);
    if (!p) return;
    setStartDate(isoDaysAgo(p.days));
    setEndDate(isoToday());
  };

  const generateInsight = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/sales-patterns/ai-insight`, {
        start_date: startDate,
        end_date: endDate,
      });
      setInsight(res.data.insight);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to generate AI insight");
    } finally {
      setAiLoading(false);
    }
  };

  const currentTabKey = TAB_KEYS[tab];
  const rows = data ? data[currentTabKey] : [];
  const highlights = data ? data.highlights[currentTabKey] : null;

  return (
    <Card data-testid="sales-pattern-card" className="mb-8">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle className="text-xl font-semibold flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-purple-600" />
              Sales Pattern Analysis
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">Spot busy vs quiet times of the month & week</p>
          </div>

          <div className="flex flex-wrap gap-2 items-end">
            {RANGE_PRESETS.map((p) => (
              <Button
                key={p.key}
                size="sm"
                variant={preset === p.key ? "default" : "outline"}
                onClick={() => applyPreset(p.key)}
                data-testid={`pattern-range-${p.key}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>

        {preset === "custom" && (
          <div className="flex flex-wrap gap-3 mt-3">
            <div>
              <Label className="text-xs">Start</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="pattern-start-date" />
            </div>
            <div>
              <Label className="text-xs">End</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="pattern-end-date" />
            </div>
            <Button size="sm" onClick={fetchData} className="self-end" data-testid="pattern-apply-range">Apply</Button>
          </div>
        )}
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading pattern...
          </div>
        ) : !data || (data.totals.transactions === 0) ? (
          <div className="text-center py-12 text-gray-500">
            No sales data in this range yet.
          </div>
        ) : (
          <>
            <div className="text-sm text-gray-600 mb-4">
              Range: <span className="font-medium">{data.range.start}</span> to{" "}
              <span className="font-medium">{data.range.end}</span> ·{" "}
              <span className="font-medium">{data.totals.transactions.toLocaleString()}</span> transactions ·{" "}
              <span className="font-medium">{fmtR(data.totals.sales)}</span>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList data-testid="pattern-tabs">
                <TabsTrigger value="dow" data-testid="pattern-tab-dow">Day of Week</TabsTrigger>
                <TabsTrigger value="dom" data-testid="pattern-tab-dom">Day of Month</TabsTrigger>
                <TabsTrigger value="wom" data-testid="pattern-tab-wom">Week of Month</TabsTrigger>
              </TabsList>

              <TabsContent value="dow" className="mt-4">
                <Highlights h={highlights} />
                <PatternChart rows={rows} testId="pattern-chart-dow" />
              </TabsContent>
              <TabsContent value="dom" className="mt-4">
                <Highlights h={highlights} />
                <PatternChart rows={rows} testId="pattern-chart-dom" />
              </TabsContent>
              <TabsContent value="wom" className="mt-4">
                <Highlights h={highlights} />
                <PatternChart rows={rows} testId="pattern-chart-wom" />
              </TabsContent>
            </Tabs>

            <div className="mt-6 pt-4 border-t">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-medium">AI Insight (Claude)</span>
                </div>
                <Button
                  size="sm"
                  variant={insight ? "outline" : "default"}
                  onClick={generateInsight}
                  disabled={aiLoading}
                  data-testid="pattern-ai-generate"
                >
                  {aiLoading ? (<><Loader2 className="h-3 w-3 mr-2 animate-spin" />Analysing...</>) : (insight ? "Regenerate" : "Generate insight")}
                </Button>
              </div>

              {insight ? (
                <div
                  data-testid="pattern-ai-insight"
                  className="text-sm text-gray-800 bg-purple-50 border border-purple-100 rounded-lg p-4 whitespace-pre-wrap leading-relaxed"
                >
                  {insight}
                </div>
              ) : (
                <div className="text-xs text-gray-500">
                  Click "Generate insight" to get a plain-English breakdown of your busiest & slowest times, plus two suggested actions.
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesPatternCard;
